// Knowledge Book rules: discovery, chapter reveal, quiz attempts, learning.
import { CODEX } from '../config/constants';
import { CHEESES, type ChapterId } from '../data/cheeses';
import { CHAPTER_BY_ID, CODEX_BY_CHEESE, entriesInChapter, type CodexEntry } from '../data/codex';
import type { SaveState } from './GameState';

export interface AttemptQuestion {
  index: number;
  q: string;
  options: string[];
  /** Index of the correct option after shuffling */
  answer: number;
  picked: number | null;
}

export interface QuizAttempt {
  cheeseId: string;
  questions: AttemptQuestion[];
  current: number;
  correct: number;
  gemsEarned: number;
  done: boolean;
  passed: boolean;
}

export function isDiscovered(s: SaveState, cheeseId: string): boolean {
  return s.codex.discovered.includes(cheeseId);
}

export function isLearned(s: SaveState, cheeseId: string): boolean {
  return s.codex.learned.includes(cheeseId);
}

/** A chapter's title shows once any entry in it has been discovered. */
export function chapterRevealed(s: SaveState, chapter: ChapterId): boolean {
  return entriesInChapter(chapter).some((e) => isDiscovered(s, e.cheeseId));
}

export function chapterLearnedCount(s: SaveState, chapter: ChapterId): number {
  return entriesInChapter(chapter).filter((e) => isLearned(s, e.cheeseId)).length;
}

/** Why an entry cannot be discovered right now, or null when it can. */
export function discoveryBlock(s: SaveState, cheeseId: string): string | null {
  const entry = CODEX_BY_CHEESE[cheeseId];
  if (!entry) return 'Unknown cheese.';
  if (isDiscovered(s, cheeseId)) return 'Already discovered.';
  const chapter = CHAPTER_BY_ID[entry.chapter];
  if (chapter.locked) return chapter.locked;
  if (chapter.requiresLearnedFrom && chapterLearnedCount(s, chapter.requiresLearnedFrom) === 0) {
    return `Learn a ${CHAPTER_BY_ID[chapter.requiresLearnedFrom].title.replace(/ Cheeses$/, '').toLowerCase()} cheese first.`;
  }
  if (s.gold < chapter.fee) return `Discovery fee is ${chapter.fee} gold.`;
  return null;
}

export function discoveryFee(cheeseId: string): number {
  const entry = CODEX_BY_CHEESE[cheeseId];
  return entry ? CHAPTER_BY_ID[entry.chapter].fee : 0;
}

/** First attempt after discovery is free; every retest costs gold. */
export function quizCost(s: SaveState, cheeseId: string): number {
  return (s.codex.attempts[cheeseId] ?? 0) === 0 ? 0 : CODEX.RETEST_COST;
}

export function quizBlock(s: SaveState, cheeseId: string): string | null {
  if (!isDiscovered(s, cheeseId)) return 'Discover the cheese first.';
  const cost = quizCost(s, cheeseId);
  if (s.gold < cost) return `A retest costs ${cost} gold.`;
  return null;
}

function shuffled<T>(arr: T[], rng: () => number): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Build a fresh attempt with shuffled question and option order. */
export function buildAttempt(entry: CodexEntry, rng: () => number = Math.random): QuizAttempt {
  const order = shuffled(entry.quiz.map((_, i) => i), rng).slice(0, CODEX.QUESTIONS);
  const questions: AttemptQuestion[] = order.map((index) => {
    const q = entry.quiz[index];
    const opts = shuffled(q.options.map((text, i) => ({ text, i })), rng);
    return {
      index,
      q: q.q,
      options: opts.map((o) => o.text),
      answer: opts.findIndex((o) => o.i === q.answer),
      picked: null,
    };
  });
  return { cheeseId: entry.cheeseId, questions, current: 0, correct: 0, gemsEarned: 0, done: false, passed: false };
}

/**
 * Record an answer. Returns whether it was right and how many gems it earned
 * (one, the first time this question is ever answered correctly).
 */
export function answerCurrent(s: SaveState, attempt: QuizAttempt, optionIndex: number): { correct: boolean; gems: number } {
  if (attempt.done) return { correct: false, gems: 0 };
  const q = attempt.questions[attempt.current];
  if (!q || q.picked !== null) return { correct: false, gems: 0 };
  q.picked = optionIndex;
  const correct = optionIndex === q.answer;
  let gems = 0;
  if (correct) {
    attempt.correct++;
    const seen = s.codex.correct[attempt.cheeseId] ?? (s.codex.correct[attempt.cheeseId] = []);
    if (!seen.includes(q.index)) {
      seen.push(q.index);
      gems = CODEX.GEM_PER_FIRST_CORRECT;
      attempt.gemsEarned += gems;
    }
  }
  return { correct, gems };
}

export function advanceAttempt(s: SaveState, attempt: QuizAttempt): void {
  if (attempt.done) return;
  attempt.current++;
  if (attempt.current >= attempt.questions.length) {
    attempt.done = true;
    attempt.passed = attempt.correct >= CODEX.PASS_CORRECT;
    if (attempt.passed && !s.codex.learned.includes(attempt.cheeseId)) s.codex.learned.push(attempt.cheeseId);
  }
}

export function gemsCollected(s: SaveState, cheeseId: string): number {
  return (s.codex.correct[cheeseId] ?? []).length * CODEX.GEM_PER_FIRST_CORRECT;
}

/** Cheeses whose recipes the farm may run. */
export function learnedRecipes(s: SaveState): string[] {
  return s.codex.learned.filter((id) => CHEESES[id]?.process);
}
